import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { LanguageToolPage } from '@/components/astro/new-design-25/SEO/language-tool-page';
import { hreflangLinks, seo } from '@/seo/seo';

const faqTitle = 'Spanish text to speech FAQ';
const faqDescription =
  'Quick answers about converting Spanish text to speech with Listening.';
const faqs = [
  {
    question: 'Which Spanish voices are available?',
    answer:
      'Listening offers natural Spanish voices in male and female options across multiple regional variants, including voices from Spain, Mexico, and other Latin American countries. Pick the variant that matches your audience.',
  },
  {
    question: 'Does it handle academic Spanish vocabulary?',
    answer:
      'Yes. The voices are trained on Spanish academic content, with accurate pronunciation of scientific terminology, philosophical and literary vocabulary, and proper academic register.',
  },
  {
    question: 'Can I use this for Spanish language learning?',
    answer:
      'Yes. Slow the playback to 0.5x to catch every syllable, then speed up as your ear improves. Synced highlighting in the Listening app helps map sound to written form.',
  },
  {
    question: 'How many characters can I convert at once?',
    answer:
      'The free converter on this page is capped at 10,000 characters per generation. The Listening app removes this limit and handles full documents, PDFs, and unlimited pasted text.',
  },
];

const heroTitle = 'Spanish Text to Speech';
const heroDescription =
  'Spanish text to speech for academic content. Natural voices from Spain and Latin America. Perfect for research papers and educational materials.';

const featureTitle = 'the Spanish academic audio solution that helps:';
const featureSubtitle = '';
const featureDescription = '';
const featureBadge = 'Multilingual';
const featureVariant = 'default';
const featureCtaText = 'Try now';

const featureFeatures = [
  {
    title: 'Native Language Access',
    description:
      'Spanish-speaking students access academic content in their native language',
  },
  {
    title: 'Course Materials Creation',
    description:
      'International universities create Spanish course materials and accessibility content',
  },
  {
    title: 'Multilingual Research',
    description:
      'Hispanic researchers process multilingual academic content efficiently',
  },
  {
    title: 'Educational Institution Support',
    description:
      'Educational institutions serve growing Spanish-speaking student populations',
  },
];

const solutionBenefitsData = {
  title: 'Feature Bullets',
  items: [
    {
      text: 'Natural Spanish voices from Spain, Mexico, and Latin America for academic content',
      highlight: 'Spain, Mexico, and Latin America',
    },
    {
      text: 'Perfect pronunciation of Spanish scientific and technical terminology',
      highlight: 'Perfect pronunciation',
    },
    {
      text: 'Specialized training on formal Spanish academic vocabulary and register',
      highlight: 'Specialized training',
    },
    {
      text: 'Professional quality suitable for university courses and research materials',
      highlight: 'Professional quality',
    },
  ],
};

const useCasesData = {
  title: 'Use Case Scenarios',
  description:
    'Real-world scenarios where Spanish text-to-speech transforms academic accessibility and research productivity.',
  useCases: [
    {
      title: 'Spanish University Student',
      challenge:
        'Access international research papers with Spanish language support for better comprehension',
      solutionSections: [
        {
          label: 'Spanish TTS Solution',
          content:
            'Natural Spanish voices with proper academic pronunciation from multiple Spanish-speaking regions',
        },
        {
          label: 'Generic TTS Problem',
          content:
            'Generic Spanish TTS struggles with academic vocabulary and regional pronunciation variations',
        },
      ],
    },
    {
      title: 'Latin American Research Institution',
      challenge:
        'Create accessible Spanish versions of research materials for diverse Hispanic audiences',
      solutionSections: [
        {
          label: 'Spanish TTS Solution',
          content:
            'Regional Spanish variants optimized for academic content delivery across Latin America',
        },
        {
          label: 'Generic TTS Problem',
          content:
            'Standard TTS lacks proper pronunciation of Spanish academic terminology and regional nuances',
        },
      ],
    },
    {
      title: 'International Academic Collaboration',
      challenge:
        'Bridge language barriers between Spanish and international research communities',
      solutionSections: [
        {
          label: 'Spanish TTS Solution',
          content:
            'Accurate Spanish academic vocabulary with technical term pronunciation for global collaboration',
        },
        {
          label: 'Generic TTS Problem',
          content:
            'Poor pronunciation makes Spanish academic content difficult for international audiences to understand',
        },
      ],
    },
    {
      title: 'Hispanic Educational Content Creator',
      challenge:
        'Develop high-quality Spanish educational materials for online learning platforms',
      solutionSections: [
        {
          label: 'Spanish TTS Solution',
          content:
            'Professional Spanish voices suitable for educational content delivery with formal academic register',
        },
        {
          label: 'Generic TTS Problem',
          content:
            'Basic Spanish TTS lacks the quality and formal register needed for professional educational content',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title:
      'Why Spanish Academic Content Requires Specialized Text-to-Speech Technology',
    content:
      'Spanish-speaking academic communities represent one of the largest and fastest-growing segments in global higher education, requiring text-to-speech technology that handles diverse Spanish variants, complex academic terminology, and institutional vocabulary across different Spanish-speaking regions. Generic Spanish TTS systems often struggle with academic pronunciation, technical terminology, and the formal register required for educational content.',
    contentSecondary:
      'Our Spanish text-to-speech system provides natural voices specifically trained on academic Spanish content from Spain, Mexico, and Latin America. We achieve superior accuracy on scientific terminology, proper pronunciation of Spanish academic institutions and concepts, and natural handling of formal academic Spanish that maintains credibility for educational use. This specialized approach serves Spanish-speaking students, researchers, and educational institutions requiring professional-quality Spanish audio.',
  },
  {
    title: 'Expand Academic Accessibility for Spanish-Speaking Communities',
    content:
      'Our Spanish TTS technology addresses the critical need for accessible Spanish academic content, enabling universities and educational institutions to serve growing Hispanic student populations with native-language materials. The system handles complex Spanish academic vocabulary, maintains proper pronunciation across different Spanish variants, and provides the formal tone and clarity essential for educational content credibility.',
    contentSecondary:
      'Educational institutions report significantly improved accessibility and student engagement when providing Spanish academic content through our specialized TTS system compared to generic Spanish voice tools. Whether creating Spanish course materials, translating research papers, or developing bilingual educational programs, our technology delivers the linguistic accuracy and professional quality necessary for serious Spanish academic applications.',
  },
];

export const Route = createFileRoute('/_marketing/_seo/spanish-text-to-speech')(
  {
    beforeLoad: assertPublicToolsEnabled,
    head: () => ({
      meta: [
        ...seo({
          title:
            'Spanish Text to Speech — Natural Spain & Latin American Voices | Listening',
          description:
            'Spanish text to speech with natural voices from Spain and Latin America. Built for academic content, language learning, and professional Spanish audio. Free demo.',
        }),
      ],
      links: [...hreflangLinks('/spanish-text-to-speech')],
    }),
    component: () => (
      <LanguageToolPage
        converterHeading={heroTitle}
        converterSubtitle={heroDescription}
        faqDescription={faqDescription}
        faqTitle={faqTitle}
        faqs={faqs}
        featureBadge={featureBadge}
        featureCtaText={featureCtaText}
        featureDescription={featureDescription}
        featureFeatures={featureFeatures}
        featureSubtitle={featureSubtitle}
        featureTitle={featureTitle}
        featureVariant={featureVariant}
        preferredLang='es'
        seoSections={seoSections}
        solutionBenefitsData={solutionBenefitsData}
        useCasesData={useCasesData}
      />
    ),
  },
);
